<template>

<div class="container">
  <div class="tabs-selection-menu">
    <div
      class="tab"
      v-for="(tab, index) in tabs"
      :key="index"
      @click="() => setActiveTab(index)"
      :class="{ active: index === activeTabIndex }"
    >
      {{tab.label}}
    </div>
  </div>
  <div v-if="tabs[activeTabIndex].headerComponent">
    <component :is="tabs[activeTabIndex].headerComponent" />
  </div>
  <div
    :class='["content", {"scrollable": tabs[activeTabIndex].scrollable}]'
  >
    <component :is="tabs[activeTabIndex].component"  />
  </div>
</div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

defineProps(['tabs', 'scrollable'])
const activeTabIndex = ref(0);
function setActiveTab(index: number) {
  activeTabIndex.value = index;
}
</script>

<style lang="scss" scoped>
.container {
  height: 21.4em;
  /* BUI token chrome (sits inside the properties-pane card). */
  border: 1px solid var(--bui-tbl-line, #ffd9e6);
  border-radius: var(--bui-ter-radius, 8px);
  overflow: hidden;

  .content {
    /* Generic token pass so every tab pane (Format / Layers / Tags /
       Auditing) reads correctly on the card in all themes without editing
       each field component. */
    color: var(--bui-tbl-row-text, #2a2969);

    :deep(label),
    :deep(.label) {
      color: var(--bui-tbl-row-text, #2a2a30);
    }

    :deep(input[type='text']),
    :deep(input[type='number']),
    :deep(textarea) {
      color: var(--bui-ter-input-text, #1a1a1e);
      background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
      border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
    }

    :deep(input[type='checkbox']),
    :deep(input[type='radio']) {
      accent-color: var(--bui-chk-checked-fill, #de3da9);
    }

    :deep(.styled-select) {
      border-color: var(--bui-ter-input-border, rgba(0, 0, 0, 0.12));
      border-radius: var(--bui-ter-radius, 8px);
      background: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));

      select {
        color: var(--bui-ter-input-text, #1a1a1e);
      }
    }

    &.scrollable {
      overflow-y: auto;
      height: 81%;
      @include x-large-devices-down {
        height: 81%;
      }
      /* width */
      &::-webkit-scrollbar {
        width: 10px;
      }

      /* Track */
      &::-webkit-scrollbar-track {
        background: var(--bui-ter-input-fill, #f1f1f1);
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: var(--bui-tbl-line, #25294A);
        cursor: pointer;
        border-radius: 5px;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: var(--bui-tbl-detail-text, rgb(37 41 74 / 50%));
      }
    }
  }

}


.tabs-selection-menu {
  display: flex;
  align-items: center;
  background: var(--bui-tbl-head-fill, #a420c8);
}
.tab {
  height: 2.1em;
  line-height: 2.1em;
  padding: 0 0.7em;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--bui-tbl-head-text, #fffcfd);
  background: transparent;

  &.active {
    cursor: default;
    background-color: var(--bui-tbl-row-fill, #fffcfd);
    color: var(--bui-tbl-row-text, #2a2969);
  }
}
</style>